import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AssetUrls } from 'config/assets';
import { MARQUEE_CARD_SCRYFALL_IDS } from 'constants/marqueeCards';
import './EmblemCardMarquee.css';

// Scryfall "small" dimensions — set explicitly on every image so the strip
// reserves its space before/through lazy load and contributes zero CLS.
const CARD_W = 146;
const CARD_H = 204;
// Each card advances this many px past the previous card's left edge, so cards
// overlap (CARD_W - ADVANCE px of overlap). A flat, horizontal "spread".
const ADVANCE = 100;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Fisher–Yates — returns a new array, leaving the source constant untouched.
function shuffle<T>(source: readonly T[]): T[] {
  const arr = source.slice();
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Decorative ambient marquee that drifts a flat, full-viewport-width row of
 * high-value card images behind the "How It Works" envelope emblem. Fully
 * static data (no fetch); purely presentational, so the subtree is aria-hidden.
 *
 * Layout: the strip container is NOT rotated — it sits flat across the viewport.
 * Each card is individually rotated 15deg and overlaps its neighbour, reading as
 * a casual spread of cards. Solid black backings stop inter-card bleed-through.
 *
 * Seamless loop: the shuffled list is repeated as a sequence of identical copies
 * (enough to over-fill the widest viewport). The track translates by exactly one
 * sequence width (--marquee-shift), so after a loop the next copy lands precisely
 * where the previous began — no visible jump.
 */
const EmblemCardMarquee: React.FC = () => {
  // Resolve reduced-motion once on mount for a deterministic first render.
  const [reduceMotion] = useState(prefersReducedMotion);
  // Shuffle once per page load; stable across re-renders.
  const ids = useMemo(() => shuffle(MARQUEE_CARD_SCRYFALL_IDS), []);
  const rootRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);

  // Pixel width of one full card sequence (the exact loop period).
  const period = ids.length * ADVANCE;
  // Render enough identical sequences that, after shifting one period left, the
  // row still fills the widest expected viewport — required for a full-bleed
  // seamless loop. Resolved once at mount.
  const sequences = useMemo(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1920;
    return Math.max(2, Math.ceil(w / period) + 2);
  }, [period]);

  useEffect(() => {
    const el = rootRef.current;
    // Under reduced motion the animation is hard-disabled in CSS, so there is
    // nothing to pause — skip the observer entirely.
    if (!el || reduceMotion) return;

    const observer = new IntersectionObserver(
      ([entry]) => setPaused(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reduceMotion]);

  const cards: React.ReactNode[] = [];
  for (let s = 0; s < sequences; s += 1) {
    ids.forEach((id, i) => {
      cards.push(
        <div className="emblem-marquee-card" key={`${s}-${i}`}>
          <img
            className="emblem-marquee-card-img"
            src={AssetUrls.cards.getCardImage(id, 'small')}
            width={CARD_W}
            height={CARD_H}
            // NOT lazy: the track is moved by CSS transform, whose layout
            // position never changes, so iOS Safari's lazy-load (which triggers
            // off layout position) never fires for cards parked off-viewport —
            // they render as black backings. The set is a fixed ~20 images
            // (~260KB total, cached across repeated sequences), cheap to eager-load.
            alt=""
          />
        </div>
      );
    });
  }

  return (
    <div
      ref={rootRef}
      className={`emblem-marquee${paused ? ' is-paused' : ''}`}
      aria-hidden="true"
      style={{ '--marquee-shift': `${period}px` } as React.CSSProperties}
    >
      <div className="emblem-marquee-track">{cards}</div>
    </div>
  );
};

export default EmblemCardMarquee;
